import {
  CircleAlert,
  CircleCheck,
  RefreshCw,
  MoreVertical,
  Pencil,
  Share2,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Button } from '../atoms/Button';
import { InspectionModelHelpers } from '../../helpers/inspection-model-helpers';
import { useEffect, useState } from 'react';
import { inspectionDB } from '../../data/database.structure';
import { EquipmentModel } from '../../data/types';
import { EllipsisText } from '../atoms/EllipsisText';
import CustomDropdown, { ArrowDropdown, DropdownItem } from './CustomDropdown';
import { useNavigate } from 'react-router';
import InspectionReportService from '../../services/inspection/report.service';
import {
  DeliveryState,
  inspectionRecordService,
} from '../../services/inspection/inspectionRecordService';
import { syncDrainService } from '../../services/inspectionSync/initSyncDrain';
import { toast } from '../../store/toast';
import { EToastType, TOAST_DURATIONS } from './ToastMessage';
import { InspectionViewModel } from '../../data/viewmodels.types';
import { EquipmentType, TaskStatus } from '../../data/enums';
import { useEquipmentTypes } from '../../store/equipmentTypesCached';
import DateHelpers from '../../helpers/dateHelpers';
import useInspectionTableOfContentsIndicators from '../../hooks/progressIndicators/useInspectionTableOfContentsIndicators';

interface InspectionCardProps {
  model: InspectionViewModel;
  showMenu?: boolean;
}

/**
 * Progress bar for in-progress cards only (the RESUME tile's key question:
 * "how far did I get?"). Reuses the ToC screen's per-section indicators -
 * per-inspection Dexie work, so the caller must never render this for the
 * submitted list.
 */
const InspectionCardProgress = ({ inspectionId }: { inspectionId: string }) => {
  const {
    inspectionDateIndicator,
    machinePhotoIndicator,
    operationIndicator,
    inspectionSummaryIndicator,
    undercarriageIndicator,
    bucketIndicator,
    hasBucket,
  } = useInspectionTableOfContentsIndicators(inspectionId);

  const sections = [
    inspectionDateIndicator,
    machinePhotoIndicator,
    operationIndicator,
    ...(undercarriageIndicator !== undefined ? [undercarriageIndicator] : []),
    ...(hasBucket ? [bucketIndicator] : []),
    inspectionSummaryIndicator,
  ];
  const done = sections.filter((s) => s === TaskStatus.completed).length;
  // Half credit for started-but-unfinished sections so the bar moves as you
  // work, not only when a whole section lands; the label counts whole sections.
  const started = sections.filter((s) => s === TaskStatus.inProgress).length;

  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-line/40">
        <div
          className="h-full rounded-full bg-brand transition-all"
          style={{
            width: `${((done + started * 0.5) / sections.length) * 100}%`,
          }}
        />
      </div>
      <span className="shrink-0 text-xs text-inkMuted">
        {done} of {sections.length} sections
      </span>
    </div>
  );
};

/** Labelled chips, never colour alone. */
const DeliveryLabel = ({ state }: { state: DeliveryState }) => {
  switch (state) {
    case DeliveryState.InProgress:
      // No red here: "not synced yet" is the NORMAL state of work in
      // progress. The affordance says what tapping does instead.
      return (
        <span className="shrink-0 text-sm font-bold text-brand">
          Continue →
        </span>
      );
    case DeliveryState.Reopened:
      return (
        <span className="flex shrink-0 items-center gap-1 rounded-full border border-brand/40 bg-brandLight/10 px-2 py-0.5 text-xs font-bold text-brand">
          <Pencil className="size-3.5" /> Reopened
        </span>
      );
    case DeliveryState.NeedsAttention:
      return (
        <span className="flex shrink-0 items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700">
          <CircleAlert className="size-3.5" /> Couldn't send
        </span>
      );
    case DeliveryState.Queued:
    case DeliveryState.PhotosPending:
      return (
        <span className="flex shrink-0 items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">
          <RefreshCw className="size-3.5" /> Waiting to send
        </span>
      );
    case DeliveryState.Delivered:
      return (
        <span className="flex shrink-0 items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-bold text-green-800">
          <CircleCheck className="size-3.5" /> Synced
        </span>
      );
  }
};

export const InspectionCard = ({
  model,
  showMenu = true,
}: InspectionCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigator = useNavigate();

  const deliveryState = inspectionRecordService.deliveryState(model.inspection);
  const isSubmitted = inspectionRecordService.isSubmitted(model.inspection);

  const [dropdownItems, setDropdownItems] = useState<DropdownItem[]>([]);
  const { data: equipmentTypes, isLoading: isLoadingEquipmentTypes } =
    useEquipmentTypes();

  const getInspectorAvatarName = (fullName: string) => {
    const names = fullName.trim().split(' ').filter(Boolean); // Remove extra spaces
    if (names.length === 0) return 'UK'; // default profile name
    if (names.length === 1)
      return names[0].substring(0, 2)?.toUpperCase() ?? 'UK'; // case username is email

    const first = names[0][0];
    const last = names[names.length - 1][0];
    return (first + last).toUpperCase();
  };

  useEffect(() => {
    // The reports exist on the server only once the Inspection is Delivered.
    const loadReportItems = async (): Promise<DropdownItem[]> => {
      const items: DropdownItem[] = [];

      if (deliveryState === DeliveryState.Delivered) {
        if (isLoadingEquipmentTypes || equipmentTypes === undefined) {
          return items;
        }

        const equipment: EquipmentModel[] = [];

        if (model.asset) {
          const e = await inspectionDB.equipments
            .where('assetId')
            .equals(model.asset?.id ?? -1)
            .toArray();

          equipment.push(...e);
        } else if (model.mineLocation) {
          const e = await inspectionDB.equipments
            .where('mineLocationId')
            .equals(model.mineLocation?.mineLocation.id ?? -1)
            .toArray();

          equipment.push(...e);
        } else {
          return items;
        }

        const undercarriageEquipmentType = equipmentTypes.find(
          (ep) => ep.name === EquipmentType.crawler,
        );
        const bucketEquipmentType = equipmentTypes.find(
          (ep) => ep.name === EquipmentType.bucket,
        );

        // If the Inspection has an Undercarriage, then add the Share UC Report option to dropdownItems
        if (
          undercarriageEquipmentType !== undefined &&
          equipment.some(
            (e) => e.equipmentTypeId === undercarriageEquipmentType.id,
          )
        ) {
          items.push({
            title: 'Share UC Report',
            icon: <Share2 className="mr-2 h-4 w-4 text-brand" />,
            onClick: async () => {
              setIsOpen(false);
              await InspectionReportService.downloadInspectionReport({
                inspectionId: model.inspection.id,
                exportFormats: ['Word'],
                reportType: 'UndercarriageInspection',
                responseType: 'File',
              });
            },
          });
        }

        // If the Inspection has Bucket, then add the Share BKT Report option to dropdownItems
        if (
          bucketEquipmentType !== undefined &&
          equipment.some((e) => e.equipmentTypeId === bucketEquipmentType.id)
        ) {
          items.push({
            title: 'Share GET/BKT Report',
            icon: <Share2 className="mr-2 h-4 w-4 text-brand" />,
            onClick: async () => {
              setIsOpen(false);
              await InspectionReportService.downloadInspectionReport({
                inspectionId: model.inspection.id,
                exportFormats: ['Word'],
                reportType: 'BucketInspection',
                responseType: 'File',
              });
            },
          });
        }
      }

      return items;
    };

    const loadMenuItems = async (): Promise<void> => {
      const items: DropdownItem[] = [];

      if (isSubmitted) {
        items.push(...(await loadReportItems()));
        items.push({
          title: 'Reopen Inspection',
          icon: <Pencil className="mr-2 h-4 w-4 text-brand" />,
          onClick: async () => {
            await inspectionRecordService.reopen(model.inspection.externalId);
            setIsOpen(false);
          },
        });
      } else {
        items.push({
          title: 'Delete Inspection',
          icon: <Trash2 className="mr-2 h-4 w-4 text-red-500" />,
          dialog: {
            title: 'Delete Inspection',
            description: 'Are you sure you want to delete this inspection?',
            leftBtn: 'Cancel',
            rightBtn: 'Delete',
            leftClick: () => {
              setIsOpen(false);
            },
            rightClick: async () => {
              await inspectionRecordService.delete(model.inspection.externalId);
              setIsOpen(false);
            },
          },
        });
      }

      if (deliveryState === DeliveryState.Reopened) {
        items.push({
          title: 'Cancel Inspection',
          icon: <XCircle className="mr-2 h-4 w-4 text-amber-500" />,
          dialog: {
            title: 'Cancel Inspection Changes',
            description:
              'Are you sure you want to cancel this inspection? Changes will be lost.',
            leftBtn: 'Keep Inspection Open',
            rightBtn: 'Cancel Inspection',
            leftClick: () => {
              setIsOpen(false);
            },
            rightClick: async () => {
              try {
                await inspectionRecordService.cancelReopen(
                  model.inspection.externalId,
                );
              } catch (error) {
                console.error('Cancelling the reopen failed:', error);
                toast.add({
                  message:
                    'The inspection could not be put back. Your changes and the original are both kept; please try again.',
                  type: EToastType.alert,
                  isOpen: true,
                  options: { duration: TOAST_DURATIONS.VALIDATION },
                });
              } finally {
                setIsOpen(false);
              }
            },
          },
        });
      }

      setDropdownItems(items);
    };

    void loadMenuItems();
  }, [
    model,
    deliveryState,
    isSubmitted,
    equipmentTypes,
    isLoadingEquipmentTypes,
  ]);

  // Two cards in one skin: an In progress tab card is a RESUME tile (identity,
  // how far you got, continue affordance), a submitted card is a RECORD tile
  // (identity, when, where it stands in reaching the server). Hierarchy: the
  // title names the machine, but the DISCRIMINATORS - customer · site and the
  // date - sit right under it; model/type/category share one muted line.
  return (
    <div
      // No z-index here: as a grid item, z-0 creates a stacking context that
      // traps the delete/cancel confirmation dialog (fixed z-50, rendered
      // inside the card) beneath later sibling cards (#10549).
      className={`mb-4 h-full cursor-pointer rounded-lg border border-line bg-white p-4 shadow-sm transition-all hover:border-brand/40 hover:shadow-md active:bg-brandLight/10 ${
        isSubmitted ? '' : 'border-l-4 border-l-brand'
      }`}
      onClick={() => {
        navigator(
          '/inspections/' +
            model.inspection.externalId +
            (isSubmitted ? '/complete' : ''),
        );
      }}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="max-w-[calc(100%-2rem)] truncate text-lg font-bold text-ink">
              <EllipsisText
                text={
                  model.asset?.name ||
                  model.mineLocation?.mineLocation.name ||
                  'Unknown'
                }
                lines={1}
                className="max-w-full"
              />
            </span>
            {showMenu && (
              <CustomDropdown
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                className="top-9"
                dropdownArrow={
                  <ArrowDropdown isOpen={isOpen} style={{ right: 5 }} />
                }
                dropdownItems={dropdownItems}
              >
                <Button
                  variant="ghost"
                  className="z-0 p-1!"
                  aria-label="Inspection options"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                  }}
                >
                  <MoreVertical className="z-0 size-6 text-ink" />
                </Button>
              </CustomDropdown>
            )}
          </div>
          <div className="max-w-full truncate text-sm text-ink">
            {model.asset?.customerName ||
              model.mineLocation?.customerName ||
              'Unknown customer'}{' '}
            ·{' '}
            {model.asset?.siteName ||
              model.mineLocation?.siteName ||
              'Unknown site'}
          </div>
          <div className="max-w-full truncate text-sm text-inkMuted">
            {model.asset && (
              <>
                {model.asset?.model?.name} · {model.asset?.model?.assetType}{' '}
                ·{' '}
              </>
            )}
            {model.mineLocation && <>Yard · </>}
            {InspectionModelHelpers.formatInspectionCategoryTypeTitle(
              model.inspection.inspectionCategoryTypes,
            )}
          </div>

          {!isSubmitted && (
            <InspectionCardProgress
              inspectionId={model.inspection.externalId}
            />
          )}

          {/* Byline: the inspector's NAME in plain sight - it used to live in
              a hover tooltip, which touch (gloved) users can never see. */}
          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center">
              <div className="mr-2 flex size-8 shrink-0 items-center justify-center rounded-full bg-primaryColor text-white">
                {getInspectorAvatarName(model.inspection.inspectorName)}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm text-ink">
                  {model.inspection.inspectorName || 'Unknown inspector'}
                </div>
                <div className="truncate text-xs text-inkMuted">
                  {isSubmitted ? (
                    DateHelpers.displayShortTimestamp({
                      timestamp: model.inspection.inspectionDate ?? new Date(),
                      convertToCommonTime: model.inspection.isSynced,
                    })
                  ) : (
                    <>
                      Started{' '}
                      {DateHelpers.displayShortTimestamp({
                        timestamp:
                          model.inspection.inspectionDate ?? new Date(),
                        includeTime: false,
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>
            <DeliveryLabel state={deliveryState} />
          </div>

          {deliveryState === DeliveryState.NeedsAttention && (
            <div className="mt-2 flex items-start justify-between gap-2">
              <p className="min-w-0 text-sm break-words whitespace-pre-line text-red-700">
                {model.inspection.sendFailures?.reason}
              </p>
              <Button
                variant="outline"
                className="shrink-0 px-3! py-1.5! text-sm"
                prefixIcon={<RefreshCw className="size-4" />}
                onClick={async (e) => {
                  e.stopPropagation();
                  await syncDrainService.retry(model.inspection.externalId);
                }}
              >
                Retry
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
